import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, AppBar, Toolbar, Typography, IconButton, Box, Button, LinearProgress,
  Container, Paper, Radio, RadioGroup, FormControlLabel, MenuItem, TextField,
  CircularProgress, Alert
} from '@mui/material';
import { Close as CloseIcon, NavigateNext, NavigateBefore, Assignment } from '@mui/icons-material';
import { CHAPTERS, UI, localized } from '../../data/questionnaireContent';
import { assessments } from '../../services/api';
import QuestionCard from './QuestionCard';
import AssessmentResults from './AssessmentResults';

// Aplatit les questions en y rattachant l'intro de chapitre (sur la 1re question)
function flattenQuestions() {
  const flat = [];
  CHAPTERS.forEach((ch) => {
    ch.questions.forEach((q, qi) => {
      flat.push({ q, thematique: ch.thematique, chapterIntro: qi === 0 ? ch.intro : null });
    });
  });
  return flat;
}

function AssessmentWizard({ open, onClose, siteId, buildings = [], onProposalsAccepted, resumeAssessment }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const flat = useMemo(flattenQuestions, []);

  const [step, setStep] = useState('intro'); // intro | questions | results
  const [scope, setScope] = useState('site'); // 'site' | batimentId
  const [assessmentId, setAssessmentId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // (Ré)initialisation à l'ouverture
  useEffect(() => {
    if (!open) return;
    setError('');
    if (resumeAssessment) {
      setStep('questions');
      setAssessmentId(resumeAssessment.id);
      setScope(resumeAssessment.batiment_id || 'site');
      setAnswers(resumeAssessment.answers || {});
      setCurrent(0);
    } else {
      setStep('intro');
      setAssessmentId(null);
      setScope('site');
      setAnswers({});
      setCurrent(0);
      setProposals([]);
    }
  }, [open, resumeAssessment]);

  const batimentId = scope === 'site' ? null : scope;

  const handleBegin = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await assessments.create({ site_id: siteId, batiment_id: batimentId });
      setAssessmentId(res.data.id);
      setStep('questions');
      setCurrent(0);
    } catch (e) {
      console.error(e);
      setError(localized(UI.startError, lang));
    } finally {
      setBusy(false);
    }
  };

  const recordAnswer = (value) => {
    const key = flat[current].q.key;
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (assessmentId) {
      assessments.saveDraft(assessmentId, next).catch((e) => console.error('autosave', e));
    }
  };

  const goNext = () => {
    if (current < flat.length - 1) setCurrent(current + 1);
  };
  const goPrev = () => {
    if (current > 0) setCurrent(current - 1);
  };

  const handleComplete = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await assessments.complete(assessmentId, answers, lang);
      setProposals(res.data.proposals || []);
      setStep('results');
    } catch (e) {
      console.error(e);
      setError(localized(UI.completeError, lang));
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async (selectedKeys) => {
    setCreating(true);
    setError('');
    try {
      const res = await assessments.accept(assessmentId, selectedKeys);
      if (onProposalsAccepted) onProposalsAccepted(res.data.count, (res.data.skipped || []).length);
      onClose(true);
    } catch (e) {
      console.error(e);
      setError(localized(UI.acceptError, lang));
    } finally {
      setCreating(false);
    }
  };

  const handleQuit = () => {
    if (step === 'questions' && Object.keys(answers).length > 0) {
      if (!window.confirm(localized(UI.quitConfirm, lang))) return;
    }
    onClose(false);
  };

  const total = flat.length;
  const progress = step === 'questions' ? ((current + 1) / total) * 100 : step === 'results' ? 100 : 0;
  const item = flat[current];
  const isLast = current === total - 1;

  return (
    <Dialog fullScreen open={open} onClose={handleQuit}>
      <AppBar position="sticky" elevation={0} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Toolbar>
          <Assignment sx={{ mr: 1.5 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {localized(UI.panelTitle, lang)}
          </Typography>
          <IconButton color="inherit" onClick={handleQuit} edge="end">
            <CloseIcon />
          </IconButton>
        </Toolbar>
        {step === 'questions' && (
          <LinearProgress variant="determinate" value={progress} sx={{ height: 4 }} />
        )}
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4, pb: 12 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Étape 1 : accueil + périmètre */}
        {step === 'intro' && (
          <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              {localized(UI.welcomeTitle, lang)}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
              {localized(UI.welcomeText, lang)}
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'left', mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                {localized(UI.chooseScope, lang)}
              </Typography>
              <RadioGroup
                value={scope === 'site' ? 'site' : 'building'}
                onChange={(e) => setScope(e.target.value === 'site' ? 'site' : (buildings[0]?.id || 'site'))}
              >
                <FormControlLabel value="site" control={<Radio />} label={localized(UI.scopeAllSite, lang)} />
                {buildings.length > 0 && (
                  <FormControlLabel value="building" control={<Radio />} label={localized(UI.scopeBuilding, lang)} />
                )}
              </RadioGroup>
              {scope !== 'site' && buildings.length > 0 && (
                <TextField
                  select fullWidth size="small" sx={{ mt: 1 }}
                  value={scope}
                  onChange={(e) => setScope(Number(e.target.value))}
                >
                  {buildings.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {localized(UI.building, lang)} {b.numero_batiment}{b.type_batiment ? ` — ${b.type_batiment}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Paper>

            <Button
              variant="contained" size="large"
              onClick={handleBegin} disabled={busy}
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ px: 5, py: 1.5, fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              {localized(UI.begin, lang)}
            </Button>
          </Box>
        )}

        {/* Étape 2 : questions */}
        {step === 'questions' && item && (
          <Box>
            {item.chapterIntro && (
              <Alert icon={false} severity="info" sx={{ mb: 3, borderRadius: 2, maxWidth: 720, mx: 'auto' }}>
                {localized(item.chapterIntro, lang)}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
              {localized(UI.questionOf, lang).replace('{c}', current + 1).replace('{t}', total)}
            </Typography>

            <QuestionCard
              question={item.q}
              answer={answers[item.q.key]}
              onAnswer={recordAnswer}
              lang={lang}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 720, mx: 'auto', mt: 4 }}>
              <Button startIcon={<NavigateBefore />} onClick={goPrev} disabled={current === 0}>
                {localized(UI.previous, lang)}
              </Button>
              {isLast ? (
                <Button
                  variant="contained" onClick={handleComplete} disabled={busy}
                  startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)' }}
                >
                  {localized(UI.finish, lang)}
                </Button>
              ) : (
                <Button variant="contained" endIcon={<NavigateNext />} onClick={goNext}
                  sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  {localized(UI.next, lang)}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Étape 3 : résultats */}
        {step === 'results' && (
          <AssessmentResults
            proposals={proposals}
            onAccept={handleAccept}
            creating={creating}
            lang={lang}
          />
        )}
      </Container>
    </Dialog>
  );
}

export default AssessmentWizard;
